import { useMemo, useState } from "react";
import type { WageCode } from "@avg/shared";
import { formatLocalISO, parseISO } from "../lib/dates";
import { countCalendarDays, countWorkdays, holidaysForYear } from "../lib/holidays";
import { useT, useWageCodeLabel } from "../i18n/I18nContext";

interface Props {
  wageCodes: WageCode[];
  monthBounds: { firstDay: string; lastDay: string };
  defaultDate?: string;
  onSubmit: (entry: {
    fromDate: string;
    toDate: string;
    wageCodeCode: string;
    hours: number | null;
    note: string | null;
  }) => Promise<void> | void;
  onCancel?: () => void;
  busy?: boolean;
}

export function EntryForm({
  wageCodes,
  monthBounds,
  defaultDate,
  onSubmit,
  onCancel,
  busy,
}: Props) {
  const t = useT();
  const wageCodeLabel = useWageCodeLabel();
  const fallback = defaultDate ?? monthBounds.firstDay;
  const [fromDate, setFromDate] = useState<string>(fallback);
  const [toDate, setToDate] = useState<string>(fallback);
  const [wageCodeCode, setWageCodeCode] = useState<string>(
    wageCodes.find((w) => w.code !== "NONE")?.code ?? "200",
  );
  const [hours, setHours] = useState<string>("");
  const [note, setNote] = useState<string>("");

  const wage = useMemo(
    () => wageCodes.find((w) => w.code === wageCodeCode),
    [wageCodes, wageCodeCode],
  );

  const counts = useMemo(() => {
    if (!fromDate || !toDate || fromDate > toDate) return null;
    const f = parseISO(fromDate);
    const tDate = parseISO(toDate);
    return {
      calendar: countCalendarDays(f, tDate),
      workdays: countWorkdays(f, tDate),
    };
  }, [fromDate, toDate]);

  const holidaysInRange = useMemo(() => {
    if (!fromDate || !toDate || fromDate > toDate) return [];
    const f = parseISO(fromDate);
    const tDate = parseISO(toDate);
    const years = new Set<number>();
    for (let y = f.getFullYear(); y <= tDate.getFullYear(); y++) years.add(y);
    const result: { date: string; name: string }[] = [];
    for (const y of years) {
      for (const h of holidaysForYear(y)) {
        if (h.date >= fromDate && h.date <= toDate) {
          result.push({ date: h.date, name: h.name });
        }
      }
    }
    return result;
  }, [fromDate, toDate]);

  const canSubmit = !!wage && fromDate <= toDate && (!wage.requiresHours || hours.trim() !== "");

  return (
    <form
      onSubmit={async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        await onSubmit({
          fromDate,
          toDate,
          wageCodeCode,
          hours: hours.trim() ? Number(hours) : null,
          note: note.trim() || null,
        });
        setHours("");
        setNote("");
      }}
      className="space-y-4"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="label">{t("entry.reason")}</label>
          <select
            className="input"
            value={wageCodeCode}
            onChange={(e) => setWageCodeCode(e.target.value)}
          >
            {wageCodes
              .filter((w) => w.code !== "NONE")
              .map((w) => (
                <option key={w.code} value={w.code}>
                  {wageCodeLabel(w.code, w.description)} ({w.code})
                </option>
              ))}
          </select>
          {wage?.requiresHours && (
            <div className="mt-2">
              <label className="label">{t("entry.hours")}</label>
              <input
                type="number"
                step="0.25"
                min="0"
                className="input"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                placeholder={t("entry.hoursPh")}
                required
              />
            </div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label">{t("entry.from")}</label>
            <input
              type="date"
              className="input"
              value={fromDate}
              min={monthBounds.firstDay}
              max={monthBounds.lastDay}
              onChange={(e) => {
                setFromDate(e.target.value);
                if (toDate < e.target.value) setToDate(e.target.value);
              }}
              required
            />
          </div>
          <div>
            <label className="label">{t("entry.to")}</label>
            <input
              type="date"
              className="input"
              value={toDate}
              min={fromDate || monthBounds.firstDay}
              max={monthBounds.lastDay}
              onChange={(e) => setToDate(e.target.value)}
              required
            />
          </div>
        </div>
      </div>

      {counts && (
        <div className="rounded-lg border border-portal-surface-tint bg-portal-surface-tint/50 px-3 py-2 text-sm text-portal-primary">
          <strong>{counts.workdays}</strong>{" "}
          {counts.workdays === 1
            ? t("entry.workdays_one")
            : t("entry.workdays_many")}{" "}
          {t("entry.workdaysSuffix")}
          {counts.calendar !== counts.workdays && (
            <span className="text-text-secondary">
              {" "}
              ·{" "}
              {t("entry.calendarDaysTotal", { count: counts.calendar })}
            </span>
          )}
          {holidaysInRange.length > 0 && (
            <div className="mt-1 text-xs text-text-secondary">
              {t("entry.holidaysInRange")}{" "}
              {holidaysInRange.map((h) => `${h.date.slice(8)}/${h.date.slice(5, 7)} ${h.name}`).join(", ")}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="label">{t("entry.note")}</label>
        <input
          className="input"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder={t("entry.notePh")}
          maxLength={120}
        />
      </div>

      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary w-full sm:w-auto"
          >
            {t("common.cancel")}
          </button>
        )}
        <button
          type="submit"
          className="btn-primary w-full sm:w-auto"
          disabled={!canSubmit || busy}
        >
          {busy ? t("entry.saving") : t("entry.add")}
        </button>
      </div>
    </form>
  );
}

export function monthBoundsFor(year: number, month: number) {
  const first = new Date(year, month - 1, 1);
  const last = new Date(year, month, 0);
  return { firstDay: formatLocalISO(first), lastDay: formatLocalISO(last) };
}
